import { URLS } from '@/data';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { isErrorResponse } from '@/utils/errors/response-handler';
import { logger } from '@/utils/logging';
import { useMutation } from '@tanstack/react-query';

import {
    DepositErrorResponse,
    DepositParams,
    DepositResponseData,
    PaymentParams,
    PurchaseParams,
    PurchaseResponseData,
    getDepositHeaders,
} from './use-deposit';
import { useDepositMatchPromo } from './use-deposit-match-promo';
import { useDepositStatusStore } from './use-deposit-status-store';

const LOG_TAG = '[3DS_DEPOSIT_OR_PURCHASE]';

/**
 * Sends a request to deposit API endpoint.
 *
 * @async
 * @param {Object} payload - The data payload for the API call.
 * @param {number} payload.selectedAmount - The selected amount for deposit.
 * @param {PaymentMethod} payload.selectedPaymentMethodType - The selected payment method type.
 * @param {string} payload.currency - The currency of the amount.
 * @returns {Promise} Promise object represents the data returned from the API.
 */
const getDepositResult = async (payload: DepositParams): Promise<DepositResponseData> => {
    const { type, amount, currency, payment_token, payment_type_id } = payload;

    const params = new URLSearchParams({
        type,
        amount,
        currency,
        ...(payment_token && { payment_token }),
        ...(payment_type_id && { payment_type_id }),
    });

    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/wallet/deposit/3ds`, {
        method: 'POST',
        headers: getDepositHeaders(type),
        body: params.toString(),
    });

    const jsonResponse = await response.json();
    if (!response.ok) {
        throw jsonResponse;
    }

    return jsonResponse.data;
};

/**
 * Sends a request to purchase with 3ds API endpoint.
 * /auth/wallet/purchase/3ds
 */
const getPurchaseResult = async (payload: PurchaseParams): Promise<PurchaseResponseData> => {
    const { coin_package_id, type, payment_token, payment_type_id } = payload;

    const params = new URLSearchParams({
        coin_package_id,
        type,
        ...(payment_token && { payment_token }),
        ...(payment_type_id && { payment_type_id }),
    });

    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/wallet/purchase/3ds`, {
        method: 'POST',
        headers: getDepositHeaders(type),
        body: params.toString(),
    });

    const jsonResponse = await response.json();
    if (!response.ok) {
        throw jsonResponse;
    }

    return jsonResponse.data;
};

export const useThreedsDeposit = () => {
    const hasSocialWallet = useHasSocialWallet();
    const selectedAmount = useDepositStatusStore(state => state.selectedAmount);
    const coinPackageId = useDepositStatusStore(state => state.coinPackageId);
    const { activatePromoIfExists, clearPromo } = useDepositMatchPromo();

    return useMutation({
        mutationKey: ['getPaymentUrl'],
        mutationFn: async (params: PaymentParams) => {
            // Activate promo first if it exists then clear it from the store
            await activatePromoIfExists(LOG_TAG);

            // For social wallet products, we call purchase endpoint instead of deposit
            return hasSocialWallet
                ? getPurchaseResult({ ...params, coin_package_id: coinPackageId })
                : getDepositResult({ ...params, amount: selectedAmount });
        },
        onError: (err: DepositErrorResponse, variables) => {
            if (isErrorResponse(err)) {
                logger.warn(LOG_TAG, { ...err, ...variables });
            } else {
                const errorMessage = `An unknown error occurred while fetching deposit URL: ${String(err)}`;
                logger.warn(LOG_TAG, { message: errorMessage });
            }
        },
        onSettled: () => {
            clearPromo();
        },
    });
};
